/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { convertToString, IQueryResultSettings, ProductsDataActions } from '@msdyn365-commerce/retail-proxy';
import { getProductListLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';

import {
    createInventoryAvailabilitySearchCriteria,
    FullProductInput,
    getFullProducts,
    ProductDetailsCriteria,
    QueryResultSettingsProxy,
    ArrayExtensions,
    IOrderTemplateLinesState,
    OrderTemplateLine,
    mapAggregatedProductInventoryInformation
} from '@msdyn365-commerce-modules/retail-actions';

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetPaginatedOrderTemplateLinesInput implements IActionInput {
    public orderTemplateId: string;
    public queryResultSettings: IQueryResultSettings;
    public searchText: string;
    public getPrice: boolean = true;
    public getRating: boolean = true;

    constructor(
        orderTemplateId: string,
        queryResultSettings: IQueryResultSettings,
        searchText?: string,
        getPrice: boolean = true,
        getRating: boolean = true
    ) {
        this.orderTemplateId = orderTemplateId;
        this.queryResultSettings = queryResultSettings;
        this.searchText = searchText || '';
        this.getPrice = getPrice;
        this.getRating = getRating;
    }

    public getCacheKey = () =>
        `GetPaginatedOrderTemplateLines|${this.orderTemplateId}|${convertToString(this.queryResultSettings)}|${
            this.searchText
        }|${convertToString(this.getPrice)}|${convertToString(this.getRating)}`;

    public getCacheObjectType = () => 'GetPaginatedOrderTemplateLines';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 * @param orderTemplateId
 * @param searchText
 */
export const createGetPaginatedOrderTemplateLinesInput = (
    inputData: ICreateActionContext,
    orderTemplateId: string,
    searchText?: string
): GetPaginatedOrderTemplateLinesInput => {
    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    queryResultSettings.count = true;
    return new GetPaginatedOrderTemplateLinesInput(orderTemplateId, queryResultSettings, searchText);
};

export const getProductDetailsCriteriaFromActionInput = (inputData: GetPaginatedOrderTemplateLinesInput): ProductDetailsCriteria => {
    return {
        getPrice: inputData.getPrice ?? true,
        getRating: inputData.getRating ?? true
    };
};

/**
 * The action method for the GetPaginatedOrderTemplateLines data action.
 * @param input The action input.
 * @param context The action context.
 */
export async function getPaginatedOrderTemplateLinesAction(
    input: GetPaginatedOrderTemplateLinesInput,
    context: IActionContext
): Promise<IOrderTemplateLinesState> {
    if (!context.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order templates. User is not authenticated.');
    }

    const orderTemplateLinesState: IOrderTemplateLinesState = {
        lines: [],
        totalCount: 0
    };
    const promise = getProductListLinesAsync(
        {
            callerContext: context,
            queryResultSettings: input.queryResultSettings,
            bypassCache: 'get'
        },
        input.orderTemplateId,
        input.searchText
    );
    const productListLines = await promise;
    const productInputs: FullProductInput[] = [];
    if (ArrayExtensions.hasElements(productListLines)) {
        orderTemplateLinesState.totalCount = promise.metadata.count || productListLines.length;
        productListLines.forEach(listLine => {
            if (listLine.ProductId) {
                orderTemplateLinesState.lines.push(new OrderTemplateLine(listLine));
                productInputs.push(
                    new FullProductInput(
                        listLine.ProductId,
                        context.requestContext.apiSettings,
                        getProductDetailsCriteriaFromActionInput(input),
                        context.requestContext,
                        listLine.CatalogId
                    )
                );
            }
        });
        if (ArrayExtensions.hasElements(productInputs)) {
            const fullProductsInfo = await getFullProducts(productInputs, context)
                .then((products: FullProduct[]) => {
                    if (products) {
                        return products;
                    }
                    return <FullProduct[]>[];
                })
                .catch(error => {
                    context.trace(error);
                    context.telemetry.error(error.message);
                    context.telemetry.error('[getPaginatedOrderTemplateLinesAction] Unable to retrieve product info.');
                });
            if (fullProductsInfo) {
                for (const line of orderTemplateLinesState.lines) {
                    const fullProduct = fullProductsInfo.find(
                        product => product.ProductDetails.RecordId === line.productListLine.ProductId
                    );
                    if (!fullProduct) {
                        context.telemetry.error(
                            '[getPaginatedOrderTemplateLinesAction] Unable to find product info for an order template line.'
                        );

                        const unassortedProduct: FullProduct = {
                            ProductDetails: {
                                RecordId: line.productListLine.ProductId || 0,
                                DefaultUnitOfMeasure: line.productListLine.UnitOfMeasure?.toString() ?? '',
                                ProductTypeValue: 0,
                                BasePrice: 0,
                                Price: 0,
                                AdjustedPrice: 0
                            },
                            ProductPrice: {},
                            ProductRating: {}
                        };
                        line.fullProduct = unassortedProduct;

                        line.error = 'UNASSORTED';
                        line.shouldDisable = true;

                        continue;
                    }
                    line.fullProduct = fullProduct;

                    if (context.requestContext.app) {
                        const productIds: number[] = orderTemplateLinesState.lines
                            .map(line => line.productListLine.ProductId!)
                            .filter(id => id);
                        const searchCriteria = createInventoryAvailabilitySearchCriteria(context, productIds, true);

                        try {
                            const shippingProductAvailabilities = await ProductsDataActions.getEstimatedAvailabilityAsync(
                                { callerContext: context },
                                searchCriteria
                            );

                            if (
                                shippingProductAvailabilities &&
                                shippingProductAvailabilities.ProductWarehouseInventoryAvailabilities &&
                                shippingProductAvailabilities.ProductWarehouseInventoryAvailabilities.length > 0
                            ) {
                                const productAvailabilities = mapAggregatedProductInventoryInformation(
                                    context,
                                    shippingProductAvailabilities
                                );

                                // Iterate over lines and align each with the corresponding availability by index
                                orderTemplateLinesState.lines.forEach((line, index) => {
                                    const productAvailability = productAvailabilities[index];

                                    line.error = productAvailability.StockLevelCode === 'AVAIL' ? '' : productAvailability.StockLevelLabel;
                                    line.shouldDisable = false;
                                    if (
                                        context.requestContext.app.config.enableStockCheck === true &&
                                        productAvailability &&
                                        !productAvailability.IsProductAvailable
                                    ) {
                                        line.shouldDisable = true;
                                    }
                                });
                            }
                        } catch (error) {
                            if (error instanceof Error) {
                                const errorMessage = error.message;
                                context.trace(errorMessage);
                                context.telemetry.error(errorMessage);
                            } else {
                                context.trace(String(error));
                                context.telemetry.error('An unknown error occurred.');
                            }
                            context.telemetry.error('[getPaginatedOrderTemplateLinesAction] Unable to retrieve product availability.');
                        }
                    }
                }
            }
            return orderTemplateLinesState;
        }
        context.trace(orderTemplateLinesState.lines.length.toString());
        return orderTemplateLinesState;
    }
    return orderTemplateLinesState;
}

/**
 * The GetPaginatedOrderTemplateLines Data Action
 * Returns order templates.
 */
export const getPaginatedOrderTemplateLinesActionDataAction = createObservableDataAction<IOrderTemplateLinesState>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-paginated-order-template-lines',
    action: getPaginatedOrderTemplateLinesAction as IAction<IOrderTemplateLinesState>,
    input: createGetPaginatedOrderTemplateLinesInput
});

export default getPaginatedOrderTemplateLinesActionDataAction;
